import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { format, isToday, startOfDay } from "date-fns";
import { FiChevronDown, FiChevronUp, FiExternalLink, FiUser } from "react-icons/fi";
import AppPage from "layouts/AppPage";
import useCaminoStore from "stores/caminoStore";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useNewHireJourneyMeetingsQuery } from "features/Resources/Meeting/hooks";
import { Meeting } from "features/Resources/Meeting/models";
import { groupMeetingsByDate, upcomingDateLabel, getAttendeeDisplayLabel } from "features/Resources/Meeting/helpers";
import { GeneralMeetingTypeTag } from "design-system";
import MeetingDetailDrawer from "features/Resources/Meeting/Components/MeetingDetailDrawer";
import EmojiEmptyState from "components/EmojiEmptyState";
import LoadingDots from "components/Loading";


interface ScheduleMeetingRowProps {
  meeting: Meeting;
  isPast?: boolean;
  currentUserId?: string;
  onClick?: () => void;
}

function ScheduleMeetingRow({ meeting, isPast = false, currentUserId, onClick }: ScheduleMeetingRowProps) {
  const startDate = new Date(meeting.startTime);
  const timeLabel = format(startDate, "h:mm");
  const ampm = format(startDate, "a").toUpperCase();
  const attendeeLabel = getAttendeeDisplayLabel(meeting, currentUserId);
  const subtitleParts = [
    attendeeLabel,
    meeting.durationMinutes > 0 ? `${meeting.durationMinutes} min` : null,
  ].filter(Boolean);

  return (
    <Flex
      align="center"
      gap={4}
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      px={4}
      py={2.5}
      opacity={isPast ? 0.6 : 1}
      cursor="pointer"
      _hover={{ borderColor: "gray.300", bg: "gray.50" }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick?.(); }}
    >
      <VStack spacing={0} minW="36px" align="center" flexShrink={0}>
        <Text
          fontSize="sm"
          fontWeight="600"
          color={isPast ? "content.tertiary" : "content.primary"}
          lineHeight="1.2"
        >
          {timeLabel}
        </Text>
        <Text fontSize="2xs" color="content.tertiary" lineHeight="1.2" textTransform="uppercase">
          {ampm}
        </Text>
      </VStack>

      <Box w="1px" h="32px" bg="gray.200" flexShrink={0} />

      <Box flex="1" minW={0}>
        <Text
          fontSize="sm"
          fontWeight="500"
          color={isPast ? "content.tertiary" : "content.primary"}
          noOfLines={1}
        >
          {meeting.name}
        </Text>
        {subtitleParts.length > 0 && (
          <HStack spacing={1} mt={0.5}>
            <Icon as={FiUser} boxSize={3} color="content.tertiary" flexShrink={0} />
            <Text fontSize="xs" color="content.tertiary" noOfLines={1}>
              {subtitleParts.join(" · ")}
            </Text>
          </HStack>
        )}
      </Box>

      {/* Right column: type pill + action */}
      <VStack spacing={1} align="flex-end" flexShrink={0}>
        <GeneralMeetingTypeTag meetingType={meeting.meetingType} />
        {!isPast && meeting.videoConferencingUrl ? (
          <Link
            href={meeting.videoConferencingUrl}
            target="_blank"
            rel="noopener noreferrer"
            _hover={{ textDecoration: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <HStack spacing={1} color="brand.500">
              <Text fontSize="xs" fontWeight="500">Join</Text>
              <Icon as={FiExternalLink} boxSize={3} />
            </HStack>
          </Link>
        ) : null}
      </VStack>
    </Flex>
  );
}

const TODAY_EMPTY_STATES = [
  { emoji: "☕", line1: "No meetings today", line2: "Good time to get settled in" },
  { emoji: "🌤", line1: "A clear day", line2: "Nothing scheduled — enjoy the focus" },
  { emoji: "✨", line1: "Free today", line2: "Use the time to explore and get comfortable" },
  { emoji: "📖", line1: "Nothing on the calendar", line2: "A good day to work through your onboarding tasks" },
];

export default function NewHireSchedulePage() {
  const currentUser = useCaminoStore((state) => state.currentUser);
  const { data: journey, isLoading: isLoadingJourney } = useNewHireJourneyDetailQuery(
    currentUser?.newHireJourneyId,
  );
  const { data: meetings = [] } = useNewHireJourneyMeetingsQuery(journey?.id || "", {
    enabled: !!journey?.id,
  });
  const { isOpen: pastOpen, onToggle: togglePast } = useDisclosure();

  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  if (isLoadingJourney) {
    return (
      <AppPage>
        <LoadingDots />
      </AppPage>
    );
  }

  if (!journey) {
    return (
      <AppPage>
        <Text color="content.tertiary">No journey found</Text>
      </AppPage>
    );
  }

  const today = startOfDay(new Date());

  const scheduledMeetings = meetings
    .filter((m) => m.status === "scheduled" && m.startTime)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const todaysMeetings = scheduledMeetings.filter((m) => isToday(new Date(m.startTime)));
  const upcomingMeetings = scheduledMeetings.filter(
    (m) => new Date(m.startTime) > today && !isToday(new Date(m.startTime)),
  );
  const pastMeetings = meetings
    .filter((m) => m.status === "completed" && m.startTime)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  const upcomingGroups = groupMeetingsByDate(upcomingMeetings);
  const pastGroups = groupMeetingsByDate(pastMeetings);

  return (
    <AppPage>
      <VStack spacing={6} align="stretch" pb={8}>
        <Text fontSize="2xl" fontWeight="bold" color="content.primary">
          My Schedule
        </Text>

        {/* Today */}
        <VStack spacing={3} align="stretch">
          <Text fontSize="sm" fontWeight="600" color="content.primary">
            {`Today · ${format(new Date(), "EEE, MMM d")}`}
          </Text>
          {todaysMeetings.length === 0 ? (
            <EmojiEmptyState {...TODAY_EMPTY_STATES[new Date().getDay() % TODAY_EMPTY_STATES.length]} />
          ) : (
            <VStack spacing={2} align="stretch">
              {todaysMeetings.map((m) => (
                <ScheduleMeetingRow key={m.id} meeting={m} currentUserId={currentUser?.id} onClick={() => setSelectedMeeting(m)} />
              ))}
            </VStack>
          )}
        </VStack>

        {/* Upcoming */}
        <VStack spacing={4} align="stretch">
          <Text fontSize="sm" fontWeight="600" color="content.primary">
            Upcoming
          </Text>
          {upcomingGroups.length === 0 ? (
            <Text fontSize="sm" color="content.tertiary">
              No upcoming meetings
            </Text>
          ) : (
            upcomingGroups.map(({ key, date, meetings: groupMeetings }) => (
              <VStack key={key} spacing={2} align="stretch">
                <Text fontSize="xs" color="content.tertiary" fontWeight="500">
                  {upcomingDateLabel(date)}
                </Text>
                {groupMeetings.map((m) => (
                  <ScheduleMeetingRow key={m.id} meeting={m} currentUserId={currentUser?.id} onClick={() => setSelectedMeeting(m)} />
                ))}
              </VStack>
            ))
          )}
        </VStack>

        {/* Past — collapsed by default */}
        {pastMeetings.length > 0 && (
          <Box>
            <Button
              onClick={togglePast}
              w="100%"
              justifyContent="space-between"
              variant="ghost"
              bg="gray.50"
              _hover={{ bg: "gray.100" }}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              px={4}
              h="auto"
              py={3}
              fontSize="sm"
              fontWeight="500"
              color="content.primary"
              rightIcon={<Icon as={pastOpen ? FiChevronUp : FiChevronDown} boxSize={4} />}
              aria-expanded={pastOpen}
            >
              {`Past meetings (${pastMeetings.length})`}
            </Button>
            <Collapse in={pastOpen} animateOpacity>
              {/* TODO: if past meetings commonly exceed ~10, add "Show earlier" pagination here */}
              <VStack spacing={4} align="stretch" pt={4}>
                {pastGroups.map(({ key, date, meetings: groupMeetings }) => (
                  <VStack key={key} spacing={2} align="stretch">
                    <Text fontSize="xs" color="content.tertiary" fontWeight="500">
                      {format(date, "EEE, MMM d")}
                    </Text>
                    {groupMeetings.map((m) => (
                      <ScheduleMeetingRow
                        key={m.id}
                        meeting={m}
                        isPast
                        currentUserId={currentUser?.id}
                        onClick={() => setSelectedMeeting(m)}
                      />
                    ))}
                  </VStack>
                ))}
              </VStack>
            </Collapse>
          </Box>
        )}
      </VStack>

      <MeetingDetailDrawer meeting={selectedMeeting} onClose={() => setSelectedMeeting(null)} showEditLink={false} journeyStartDate={journey?.startDate} />
    </AppPage>
  );
}
